/**
 * First-match-wins explicit placement rules (permission code → production sidebar path under module).
 * Order: most specific routes first (Procurement, PMS, QHSE, …) so shared prefixes are not mis-bucketed.
 */

import { isPermanentlyHiddenPermissionCode } from '../../../../app/shared/navigation/permanentlyHiddenMenus'

export type ExplicitPlacement = {
  moduleLabel: string
  /** Path under module, e.g. ['Procurement','Requisitions'] or ['Reports','KPIs'] */
  segments: string[]
}

const M = {
  Admin: 'Admin',
  Operations: 'Operations',
  Crewing: 'Crewing',
  Technical: 'Technical',
  QHSE: 'QHSE',
  Dashboard: 'Dashboard',
  Accounts: 'Accounts',
} as const

type Rule = {test: (c: string) => boolean; p: ExplicitPlacement}

export const EXPLICIT_PLACEMENT_RULES: Rule[] = [
  // —— Technical / Procurement (before Admin / generic INVOICE / COMPANY_*) ——
  {
    test: (c) => /COMPANY_PO|COMPANY_PO_INFO|OWNER_PO_APPROVAL/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Company PO Info']},
  },
  {
    test: (c) =>
      /REQUISITION|PROCUREMENT_REQUISITION/i.test(c) ||
      (/^REQ_|_REQ_/i.test(c) && !/USER|ACCESS|CHANGE|PASSWORD|TOKEN|HTTP/i.test(c)),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Requisitions']},
  },
  {test: (c) => /^RFQ_|PROCUREMENT_RFQ|RFQ_MANAGEMENT/i.test(c), p: {moduleLabel: M.Technical, segments: ['Procurement', 'RFQ Management']}},
  {
    test: (c) => /QUOTATION_|^QUOTE_|PROCUREMENT_QUOTATION/i.test(c) && !/LINE_ITEM|TAX_CODE/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Quotation Management']},
  },
  {
    test: (c) =>
      /PURCHASE_ORDER|^PO_|_PO_|PROCUREMENT_PO|PO_SEND|PO_APPROVE|PO_REJECT|PO_LINE|PO_VENDOR/i.test(c) &&
      !/COMPANY_PO|PO_INFO|ZOHO|BOOKS/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Purchase Orders']},
  },
  {
    test: (c) => /PROCUREMENT_TRAIL|DELIVERY_TRACK|DELIVERY_TRAC|PROCUREMENT_DELIVERY/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Delivery Tracking']},
  },
  {test: (c) => /^GRN_|GOODS_RECEIPT|GOODS_RECEIV|PROCUREMENT_GRN/i.test(c), p: {moduleLabel: M.Technical, segments: ['Procurement', 'Goods Receipt']}},
  {
    test: (c) =>
      /VENDOR_INVOICE|PROCUREMENT_INVOICE|INVOICE_MANAGE|PROCUREMENT.*_INVOICE|INVOICE.*PROCUREMENT|READ_INVOICE|CREATE_INVOICE|UPDATE_INVOICE|DELETE_INVOICE|APPROVE_INVOICE|EXPORT_INVOICE|DOWNLOAD_INVOICE/i.test(
        c
      ) &&
      !/QHSE|SALES_BOOK|ZOHO|SALES_/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Invoice Management']},
  },
  {
    test: (c) =>
      /READ_PROCUREMENT_AUDIT_TRAIL|PROCUREMENT_AUDIT/i.test(c) ||
      (/AUDIT_TRAIL/i.test(c) && /PROCUREMENT|VENDOR|RFQ|QUOTATION|GRN|PO_/i.test(c)),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Audit Trail']},
  },
  {
    test: (c) => /^PROCUREMENT_LOG_VIEW$/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Audit Trail']},
  },
  {
    test: (c) =>
      /^INVOICE_VIEW$|^INVOICE_CREATE$|^INVOICE_EDIT$|^INVOICE_DELETE$|^INVOICE_DOWNLOAD$|^INVOICE_EXPORT$|^INVOICE_APPROVE$/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Invoice Management']},
  },
  {
    test: (c) =>
      /VENDOR_LIBRARY|PROCUREMENT_USER|PROCUREMENT_VENDOR|PROCUREMENT_USER_MGMT|BUNKER_TEMPLATE|QUOTATION_TEMPLATE/i.test(c) ||
      (/PROCUREMENT.*USER|INVENTORY_MANAGEMENT/i.test(c) && /PROCUREMENT|VENDOR|QUOTATION/i.test(c)),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'User Management']},
  },
  {
    test: (c) => /^USER_PERMISSION_VIEW$|^USER_PERMISSION_MANAGE$/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'User Management']},
  },
  {
    test: (c) => /^READ_PROCUREMENT_OVERVIEW$/i.test(c) || /PROCUREMENT_OVERVIEW|PROCUREMENT_DASHBOARD|MY_PROCUREMENT|^PROCUREMENT_HOME/i.test(c) || (/PROCUREMENT_/i.test(c) && /^DASHBOARD_/i.test(c)),
    p: {moduleLabel: M.Technical, segments: ['Procurement', 'Overview']},
  },

  // —— Technical — PMS / machinery (avoid JOB matching procurement quotations) ——
  {test: (c) => /^READ_PMS_DASHBOARD$|^PMS_DASHBOARD|^PMS_DASH/i.test(c), p: {moduleLabel: M.Technical, segments: ['PMS', 'Dashboard']}},
  {
    test: (c) =>
      /COMPLETE_PMS_JOB|PMS_JOB|MAINTENANCE_JOB|^PMS_.*JOB|TECHNICAL_JOB/i.test(c) && !/PROCUREMENT|QUOTATION|RFQ|REQ|REQUISITION|VENDOR/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['PMS', 'Jobs']},
  },
  {test: (c) => /RUNNING_HOUR|PMS_RUNNING/i.test(c), p: {moduleLabel: M.Technical, segments: ['PMS', 'Running Hours']}},
  {test: (c) => /CALENDAR_PLAN|PMS_CALENDAR/i.test(c), p: {moduleLabel: M.Technical, segments: ['PMS', 'Calendar Planning']}},
  {
    test: (c) => /^PMS_/i.test(c) && !/DASHBOARD|JOB|RUNNING|CALENDAR/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['PMS', 'Dashboard']},
  },
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE|IMPORT|EXPORT)_MACHINERY$/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Machinery', 'Equipment']},
  },
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE|IMPORT|EXPORT)_SUBCOMPONENT$/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Machinery', 'Subcomponent']},
  },
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE|IMPORT|EXPORT)_PART$/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Machinery', 'Part']},
  },
  {
    test: (c) =>
      /EQUIPMENT_PART|TECHNICAL_EQUIP|^SERVICE_ITEM/i.test(c) && !/PROCUREMENT|PMS_JOB|QHSE/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Machinery']},
  },
  {test: (c) => /MONTHLY_LOG|MONTH_END_TECH/i.test(c), p: {moduleLabel: M.Technical, segments: ['Monthly Log']}},
  {
    test: (c) => /TECHNICAL_DEFECT|^DEFECT_/i.test(c) && !/QHSE|OPERATIONS|PROCUREMENT/i.test(c),
    p: {moduleLabel: M.Technical, segments: ['Defects List']},
  },

  // —— QHSE ——
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE|VIEW|DOWNLOAD)_QHSE_DOCUMENT$/i.test(c) || /DOCUMENT_LIBRARY|^READ_DOCUMENT/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Document Library']},
  },
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE)_QHSE_FORM_SUBMISSION$|^FORM_SUBMISSION/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Submissions']},
  },
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE)_QHSE_FORM_TEMPLATE$|^FORM_TEMPLATE/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Static Templates']},
  },
  {
    test: (c) => /^READ_QHSE_STATIC_TEMPLATE$/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Static Templates']},
  },
  {
    test: (c) => /WORK_PERMIT/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Work Permits and Checklists']},
  },
  {
    test: (c) => /OFFICE_CERT/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Office Certificates']},
  },
  {
    test: (c) => /VESSEL_CERT|CERTIFICATE_MANAGE|QHSE_CERT/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Vessel Certificates']},
  },
  {
    test: (c) => /MANUAL_PLAN|MANUALS_AND|QHSE_MANUAL/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Manuals and Plans']},
  },
  {
    test: (c) => /QHSE_DEFECT|DEFECT.*QHSE/i.test(c) || (/DEFECT_/i.test(c) && /QHSE|INSPECTION_FINDING/i.test(c)),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Defects List']},
  },
  {
    test: (c) => /NEAR_MISS|ACCIDENT_NEAR/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Near Miss reports']},
  },
  {
    test: (c) => /SAFETY_COMMITTEE/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Safety committee meeting']},
  },
  {
    test: (c) => /RISK_ASSESSMENT|RISK_LIB/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists', 'Risk Assessment Library']},
  },
  {
    test: (c) => /CHECKLIST|FORMS_CHECKLIST|QHSE_FORM|FC_LIBRARY|FC_SUBMISSION/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Forms & Checklists']},
  },
  {
    test: (c) => /AUDIT_PLAN/i.test(c) && /VESSEL|QHSE/i.test(c) && !/PROCUREMENT/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Audits and Inspection', 'Audit Planning']},
  },
  {
    test: (c) => /AUDIT_FINDING|FINDING_INDEX/i.test(c) && /AUDIT|QHSE/i.test(c) && !/PROCUREMENT/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Audits and Inspection', 'Audit Findings']},
  },
  {
    test: (c) => /AUDIT_INDEX|^READ_AUDIT|VESSEL_AUDIT/i.test(c) && !/PROCUREMENT|PROCUREMENT_AUDIT|ZOHO/i.test(c),
    p: {moduleLabel: M.QHSE, segments: ['Audits and Inspection', 'Audit Index']},
  },
  {test: (c) => /INSPECTION_PLAN|INSPLANNING/i.test(c), p: {moduleLabel: M.QHSE, segments: ['Audits and Inspection', 'Inspection Planning']}},
  {test: (c) => /INSPECTION_FIND|INSPFIND/i.test(c), p: {moduleLabel: M.QHSE, segments: ['Audits and Inspection', 'Inspection Findings']}},
  {test: (c) => /INSPINDEX|INSPECTION_INDEX/i.test(c) && /QHSE|VESSEL_INSPECTION/i.test(c), p: {moduleLabel: M.QHSE, segments: ['Audits and Inspection', 'Inspection Index']}},
  {
    test: (c) =>
      (/MAIN_ENGINE|MONTH_END.*ENGINE|MEC_/i.test(c) && !/TECHNICAL|OPERATIONS_NAV/i.test(c)) ||
      (/MACHINERY_STATUS|MONTH_END.*MACHINERY/i.test(c) && /QHSE|MONTH_END|MONTH_END_LIB/i.test(c)) ||
      (/MONTH_END_REPORT|MONTHS_END|MONTH_END_LIB/i.test(c) && /QHSE|VESSEL|MEC/i.test(c)),
    p: {moduleLabel: M.QHSE, segments: ['Month End Reports']},
  },
  {test: (c) => /EMERGENCY_DRILL.*PLAN|DRILL_PLAN|QHSE_DRILL_PLAN/i.test(c), p: {moduleLabel: M.QHSE, segments: ['Emergency Drills', 'Drill Planner']}},
  {test: (c) => /EMERGENCY_DRILL.*REPORT|DRILL_REPORT/i.test(c), p: {moduleLabel: M.QHSE, segments: ['Emergency Drills', 'Emergency Drill Report']}},
  {test: (c) => /EMERGENCY_DRILL.*REVIEW|DRILL_REVIEW/i.test(c), p: {moduleLabel: M.QHSE, segments: ['Emergency Drills', 'Emergency Drill Review']}},


  // —— Operations —— (report builder & leaves before cargo / voyages — avoids fuzzy match on wrong category)
  {test: (c) => /REPORT_FIELD/i.test(c), p: {moduleLabel: M.Operations, segments: ['Reports', 'Report Setup', 'Field']}},
  {test: (c) => /REPORT_MENU/i.test(c), p: {moduleLabel: M.Operations, segments: ['Reports', 'Report Setup', 'Menu']}},
  {test: (c) => /REPORT_SUBMENU/i.test(c), p: {moduleLabel: M.Operations, segments: ['Reports', 'Report Setup', 'Submenu']}},
  {test: (c) => /REPORT_TEMPLATE/i.test(c), p: {moduleLabel: M.Operations, segments: ['Reports', 'Report Setup', 'Template']}},
  {
    test: (c) => /REPORT_ASSIGNMENT/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Reports', 'Report Setup', 'Assignment']},
  },

  /** KPI dashboards / assignments that are KPI route (not REPORT_ASSIGNMENT_*) */
  {
    test: (c) =>
      /^(READ|CREATE|UPDATE|DELETE|EXPORT)_KPI|_KPI_|^KPI_|REPORT_KPI|MANAGE_REPORT_KPI|ASSIGNMENT_REPORT|CII_ROW|LATEST_BY_DAY_ASSIGNMENT/i.test(
        c
      ) && !/QHSE|TECHNICAL|CREW|MACHINERY/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Reports', 'KPIs']},
  },
  {test: (c) => /VOYAGE_ABSTRACT|VOYAGE_ABSTRACT_|ABSTRACT_REPORT/i.test(c), p: {moduleLabel: M.Operations, segments: ['Reports', 'Voyage Abstract']}},
  {
    test: (c) => /CII_SUMMARY|CII_REPORT|CII_ROW|^_CII|^CII_/i.test(c) && !/CERTIFICATE|VESSEL_CERT|QHSE_CII|TECHNICAL_CII/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Reports', 'CII Summary']},
  },
  {
    test: (c) =>
      /CARGO_PLAN|CARGO_LOG|CARGO_PLAN_LOG/i.test(c) &&
      !/CARGO_OPERATION|CARGO_OPS|VESSEL_OPS_CARGO|OPERATION.*CARGO_(?!PLAN)/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Reports', 'Cargo Plan/Log']},
  },

  {
    test: (c) =>
      /VOYAGE_REPORT|VOYAGE_SUMMARY|^READ_VOYAGE$|^CREATE_VOYAGE$|^UPDATE_VOYAGE$|^DELETE_VOYAGE$|^DOWNLOAD_VOYAGE$/i.test(c) ||
      (/^VOYAGE_/i.test(c) &&
        !/ABSTRACT/i.test(c) &&
        !/CREW|SIGN_|MANNING|SEAFARER|VESSEL_CERT|VESSEL_AUDIT/i.test(c)),
    p: {moduleLabel: M.Operations, segments: ['Voyage Reports']},
  },
  {
    test: (c) =>
      /POSITION_REPORT|^LATEST_BY_DAY|READ_LATEST_BY_DAY|^LATEST_POSITION/i.test(c) &&
      !/_ASSIGNMENT|ASSIGNMENT_REPORT/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Position Reports']},
  },
  {test: (c) => /DAILY_POSITION|^DPR_/i.test(c), p: {moduleLabel: M.Operations, segments: ['Daily Position Reports']}},

  {
    test: (c) => {
      if (/REPORT_FIELD|REPORT_MENU|REPORT_SUBMENU|REPORT_TEMPLATE|REPORT_ASSIGNMENT/i.test(c)) return false
      if (/CARGO_PLAN|CARGO_LOG|CARGO_PLAN_LOG/i.test(c)) return false
      if (/OPERATIONS?_REPORT|VESSEL_OPS_REPORT|SCHEDULE_REPORT|VOYAGE_|POSITION_/i.test(c)) return false
      return (
        /\b(CARGO_OPERATION|CARGO_OPS)\b/i.test(c) ||
        /\bVESSEL_OPERATION_CARGO\b|\bVESSEL_CARGO_OPER\b|\bLOAD_DISCHARGE\b|\bCARGO_HANDLING_OPS\b|\bDISCHARGE_CARGO\b|\bLOAD_CARGO\b/i.test(c) ||
        /\bBUNKER_CONSUMPTION\b/i.test(c)
      )
    },
    p: {moduleLabel: M.Operations, segments: ['Cargo Operations']},
  },

  {
    test: (c) => /OPERATIONS_LIBRARY|VOYAGE_LIBRARY|REPORT_LIBRARY/i.test(c) && !/QHSE|TECHNICAL_FORM|PROCUREMENT/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Library']},
  },
  {test: (c) => /IN_PORT_CONSUMPTION|INPORT_CONSUMPTION|IN_PORT.*CONSUMPTION/i.test(c), p: {moduleLabel: M.Operations, segments: ['In-port Consumption Report']}},
  {test: (c) => /OFF_SHORE|OFFSHORE_REPORT/i.test(c), p: {moduleLabel: M.Operations, segments: ['Off Shore Report']}},
  {test: (c) => /FORM_INDEX|OPERATIONS_FORM|REPORT_BUILDER|CUSTOM_REPORT_LAYOUT/i.test(c), p: {moduleLabel: M.Operations, segments: ['Form Index']}},
  {test: (c) => /OPERATION_DEFECT/i.test(c) && !/QHSE|TECHNICAL/i.test(c), p: {moduleLabel: M.Operations, segments: ['Defects List']}},
  {
    test: (c) =>
      /^(READ|CREATE|UPDATE|DELETE|EXPORT|IMPORT)_REPORT(\b|_)/i.test(c) &&
      !/REPORT_FIELD|REPORT_MENU|REPORT_SUBMENU|REPORT_TEMPLATE|REPORT_ASSIGNMENT/i.test(c) &&
      !/VOYAGE_|POSITION_REPORT|DAILY_POSITION|^DPR_|CII_|ASSIGNMENT_REPORT|LATEST_BY_DAY_ASSIGNMENT|^KPI|_KPI_|TECHNICAL|QHSE|CREW|MACHINERY|PMS|PROCUREMENT|CARGO_PLAN|CARGO_LOG/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Position Reports']},
  },

  {
    test: (c) =>
      /DEFECT/i.test(c) &&
      /OPERATIONS|VOYAGE|POSITION|VESSEL_OPS|OPS_DEFECT|OPERATIONS_DEFECT/i.test(c) &&
      !/QHSE|TECHNICAL_DEFECT|TECHNICAL\b|CREW_DEFECT/i.test(c),
    p: {moduleLabel: M.Operations, segments: ['Defects List']},
  },

  // —— Crewing legacy aliases — keep their permissions attached to the final visible production leaves
  {
    test: (c) => /^MENU_PORTAGE_BILL_WORKSPACE$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {test: (c) => /^MENU_WAGE_SNAPSHOT$/i.test(c), p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']}},
  {
    test: (c) => /^MENU_ADJUSTMENTS_ALLOWANCES$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {
    test: (c) => /^MENU_DEDUCTIONS_MANAGER$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {
    test: (c) => /^MENU_REIMBURSEMENTS_MANAGER$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Claims and Reimbursement']},
  },
  {
    test: (c) => /^MENU_ALLOTMENT_BANK_DETAILS$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Allotment and Advance']},
  },
  {
    test: (c) => /^MENU_CREW_LIST_OVERVIEW$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Master']},
  },
  {
    test: (c) => /^READ_CREW_LIST_OVERVIEW$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Master']},
  },
  {
    test: (c) => /^MENU_CREW_ASSIGNMENT$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Assignment']},
  },
  {
    test: (c) => /^READ_OVERTIME_ENTRY$|^CREATE_OVERTIME_ENTRY$|^UPDATE_OVERTIME_ENTRY$|^DELETE_OVERTIME_ENTRY$/i.test(c),
    p: {
      moduleLabel: M.Crewing,
      segments: ['Crew Resthours and Overtime', 'Crew Overtime'],
    },
  },
  {
    test: (c) => /^READ_PORTAGE_BILL_WORKSPACE$|^MANAGE_PORTAGE_BILL_WORKSPACE$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {
    test: (c) => /^READ_WAGE_SNAPSHOT$|^MANAGE_WAGE_SNAPSHOT$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {
    test: (c) => /^READ_CREW_ADJUSTMENT_ALLOWANCE$|^MANAGE_CREW_ADJUSTMENT_ALLOWANCE$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {
    test: (c) => /^READ_DEDUCTIONS_MANAGER$|^MANAGE_DEDUCTIONS_MANAGER$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']},
  },
  {
    test: (c) => /^READ_REIMBURSEMENTS_MANAGER$|^MANAGE_REIMBURSEMENTS_MANAGER$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Claims and Reimbursement']},
  },
  {
    test: (c) => /^READ_ALLOTMENT_BANK_DETAILS$|^MANAGE_ALLOTMENT_BANK_DETAILS$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Allotment and Advance']},
  },
  {
    test: (c) => /^READ_TRAINING_TRACKER$|^MANAGE_TRAINING_TRACKER$/i.test(c),
    p: {
      moduleLabel: M.Crewing,
      segments: ['Crew Training, Test & Verification'],
    },
  },
  {
    test: (c) => /^READ_CREW_APPRAISALS$|^MANAGE_CREW_APPRAISALS$/i.test(c),
    p: {
      moduleLabel: M.Crewing,
      segments: ['Crew Appraisal'],
    },
  },
  {
    test: (c) => /^MENU_OVERTIME_ENTRY$/i.test(c),
    p: {
      moduleLabel: M.Crewing,
      segments: ['Crew Resthours and Overtime', 'Crew Overtime'],
    },
  },
  {
    test: (c) => /^MENU_TRAINING_TRACKER$/i.test(c),
    p: {
      moduleLabel: M.Crewing,
      segments: ['Crew Training, Test & Verification'],
    },
  },
  {
    test: (c) => /^MENU_APPRAISALS$/i.test(c),
    p: {
      moduleLabel: M.Crewing,
      segments: ['Crew Appraisal'],
    },
  },

  // —— Crewing ——
  /** Sub-leaf codes that must beat broader `READ_CREW_*`/`CREW_` matchers */
  {
    test: (c) =>
      /^READ_CREW_ASSIGNMENT$|^CREATE_CREW_ASSIGNMENT$|^UPDATE_CREW_ASSIGNMENT$|^DELETE_CREW_ASSIGNMENT$/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Assignment']},
  },
  /** Core roster / profile CRUD — must be anchored: READ_CREW is a prefix of READ_CREW_CLAIM, READ_CREW_ALLOTMENT, etc. */
  {
    test: (c) =>
      /^(READ|CREATE|UPDATE|DELETE)_CREW$/i.test(c) ||
      /^(EXPORT|DOWNLOAD)_CREW$/i.test(c) ||
      /CREW_MASTER|CREW_LIST|CREW_DETAIL|MANNING|SEAFARER|CREW_ONBOARD|CREW_OVERVIEW/i.test(c) ||
      /CREW_DOCUMENT/i.test(c) ||
      /SIGN_ON|SIGN_OFF/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Master']},
  },
  {
    test: (c) =>
      (/CREW_ASSIGN|RANK_MANAGEMENT|CREW_RANK|MASTER_RANK|^RANK_/i.test(c) || /^READ_MASTER_RANK$/i.test(c)) &&
      !/APPRAISAL|TRAINING/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Assignment']},
  },
  /** REST_HOURS_* business permissions (+ MENU_REST_* that are rest-hour gated, not MENU_OVERTIME) */
  {
    test: (c) =>
      (/REST_HOURS|RESTHOUR/i.test(c) || /REST_HOUR/i.test(c)) &&
      !/OVERTIME/i.test(c) &&
      (!/^MENU_/i.test(c) || /^MENU_REST_HOUR/i.test(c)),
    p: {moduleLabel: M.Crewing, segments: ['Crew Resthours and Overtime', 'Rest Hour Management']},
  },
  {
    test: (c) =>
      /OVERTIME|CREW_OVERTIME|WORKHOUR.*OT/i.test(c) &&
      (/CREW|REST|WORKHOUR/i.test(c) || /^MENU_OVERTIME/i.test(c)),
    p: {moduleLabel: M.Crewing, segments: ['Crew Resthours and Overtime', 'Crew Overtime']},
  },
  {test: (c) => /CREW_DEDUCT|WAGE|PORTAGE|DEDUCTION/i.test(c) && /CREW/i.test(c), p: {moduleLabel: M.Crewing, segments: ['Crew Deduction']}},
  {test: (c) => /CREW_CLAIM|REIMBURSEMENT/i.test(c) && /CREW/i.test(c), p: {moduleLabel: M.Crewing, segments: ['Crew Claims and Reimbursement']}},
  {test: (c) => /CLAIM/i.test(c) && /CREW/i.test(c) && !/INSURANCE/i.test(c), p: {moduleLabel: M.Crewing, segments: ['Crew Claims and Reimbursement']}},
  {test: (c) => /ALLOTMENT|ADVANCE|BANK/i.test(c) && /CREW/i.test(c), p: {moduleLabel: M.Crewing, segments: ['Crew Allotment and Advance']}},
  {
    test: (c) =>
      !/MANAGE_CREW_APPRAISALS$|READ_CREW_APPRAISALS$/i.test(c) &&
      /APPRAISAL/i.test(c) &&
      /CREW/i.test(c),
    p: {moduleLabel: M.Crewing, segments: ['Crew Appraisal']},
  },
  {test: (c) => /CREW_TRAINING|CREW_VERIFICATION/i.test(c) || (/TRAINING|TEST|VERIFICATION/i.test(c) && /CREW|SEAFARER/i.test(c)), p: {moduleLabel: M.Crewing, segments: ['Crew Training, Test & Verification']}},
  {test: (c) => /CREW_COMPLAINT|COMPLAINT/i.test(c) && /CREW/i.test(c), p: {moduleLabel: M.Crewing, segments: ['Crew Complaints']}},

  // —— Accounts shell (matrix catalogue) ——
  {
    test: (c) => /^READ_ACCOUNTS$/i.test(c),
    p: {moduleLabel: M.Accounts, segments: ['Accounts']},
  },

  // —— Admin ——
  {
    test: (c) => /^(READ|CREATE|UPDATE|DELETE)_COMPANY_ADMIN$/i.test(c),
    p: {moduleLabel: M.Admin, segments: ['Companies']},
  },
  {
    test: (c) => /^(COMPANY_GROUP|CGA_|SUB_COMPAN|SUBCOMPAN|COMPANY_GROUP_ADMIN)/i.test(c) || /\bCOMPANY_GROUP_ADMIN\b/i.test(c),
    p: {moduleLabel: M.Admin, segments: ['Sub-Companies']},
  },
  {
    test: (c) =>
      /^(READ_COMPANY|CREATE_COMPANY|UPDATE_COMPANY|DELETE_COMPANY)(_|$)/i.test(c) ||
      /^COMPANY_ADMIN\b/i.test(c) ||
      (/\bCOMPANY_REGISTRY\b|\bFLEET_COMPANY\b/i.test(c) && !/GROUP|PROCUREMENT|PO_/i.test(c)),
    p: {moduleLabel: M.Admin, segments: ['Companies']},
  },
  {
    test: (c) =>
      /ROLE_PERMISSION|PERMISSION_REGISTRY|READ_ROLE|CREATE_ROLE|UPDATE_ROLE|DELETE_ROLE|READ_PERMISSION|CREATE_PERMISSION|UPDATE_PERMISSION|DELETE_PERMISSION|READ_ROLE_PERMISSION|UPDATE_ROLE_PERMISSION|DELETE_ROLE_PERMISSION|CUSTOM_ROLE|ACCESS_BUNDLE|BUNDLE_DEFAULT|ASSIGNED_BUNDLE|MENU_PERMISSION|\bMENU_\b|^PERMISSION_CATALOG/i.test(c),
    p: {moduleLabel: M.Admin, segments: ['User Management']},
  },
  {
    test: (c) =>
      /READ_USER|CREATE_USER|UPDATE_USER|EDIT_USER_PASSWORD|DELETE_USER|USER_ACCESS|USER_SCOPE|USER_MANAGEMENT|\bOPERATOR_\b|\bSHORE_\b|\bCREW_USER\b|\bINVITE_USER\b/i.test(
        c
      ),
    p: {moduleLabel: M.Admin, segments: ['User Management']},
  },
  {
    test: (c) =>
      /READ_VESSEL|CREATE_VESSEL|UPDATE_VESSEL|DELETE_VESSEL|VESSEL_REGISTRY|FLEET_REGISTRY/i.test(c) && !/CREW|MANNING|SEAFARER|INSPECTION|CERT/i.test(c),
    p: {moduleLabel: M.Admin, segments: ['Vessels']},
  },
  {
    test: (c) =>
      /^VENDOR_MANAGEMENT|READ_VENDOR|CREATE_VENDOR|UPDATE_VENDOR|DELETE_VENDOR|SUPPLIER_MASTER/i.test(c) ||
      (/\bVENDOR\b/i.test(c) && /ADMIN|MASTER|REGISTRY/i.test(c) && !/PROCUREMENT|QUOTATION|RFQ|PO_/i.test(c)),
    p: {moduleLabel: M.Admin, segments: ['Vendor Management']},
  },
]

export function tryExplicitPlacement(permissionCodeUpper: string): ExplicitPlacement | null {
  const c = permissionCodeUpper.trim().toUpperCase()
  if (!c) return null
  if (isPermanentlyHiddenPermissionCode(c)) return null
  for (const r of EXPLICIT_PLACEMENT_RULES) {
    if (r.test(c)) return r.p
  }
  return null
}
